import React from 'react';
import NextLink from 'next/link';
import { makeStyles } from 'tss-react/mui';
import Typography, { TypographyProps } from '@ui/Typography';
import palette from '@ui/palette';

interface TypographyLinkProps extends Omit<TypographyProps, 'component'> {
  href: string;
  target?: string;
  rel?: string;
  children: React.ReactNode;
  inline?: boolean;
  'data-e2e'?: string;
}

const useStyles = makeStyles()(theme => ({
  link: {
    textDecoration: 'underline',
    '&:hover': {
      color: palette.mint_400,
    },
  },
}));

const TypographyLink = ({
  href,
  target,
  rel,
  children,
  inline,
  'data-e2e': dataE2e,
  ...props
}: TypographyLinkProps) => {
  const { classes } = useStyles();
  // note: was considering adding support for implicit target blank via protocol
  // but figured it's better to be explicit about it
  if (target === '_blank') {
    const externalRel = target === '_blank' ? 'noopener noreferrer' : rel;
    return (
      <Typography
        component="a"
        color={props.color || 'primary'}
        className={classes.link}
        inline={inline}
        {...props}
        {...{ href, target, rel: externalRel }}
        {...(dataE2e && { 'data-e2e': dataE2e })}
      >
        {children}
      </Typography>
    );
  }
  // otherwise assume it's an internal link
  return (
    <NextLink href={href} target={target} rel={rel} passHref legacyBehavior>
      <Typography
        component="a"
        color={props.color || 'primary'}
        className={classes.link}
        inline={inline}
        {...props}
      >
        {children}
      </Typography>
    </NextLink>
  );
};

export default TypographyLink;
